import { Box, Button, ProgressBar, Text } from '@factorialco/deprecated-design-system'

import DocumentType from 'modules/core/components/DocumentType'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { formatFileSize } from 'modules/trainings/components/Files/utils'

import { FileStatus, FileUpload, ParticipantFiles } from './types'

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200MB
const MAX_TOTAL_SIZE_PER_PARTICIPANT = 200 * 1024 * 1024 // 200MB total per participant

interface ParticipantFileRowProps {
  participant: ParticipantFiles
  onFileDrop: (membershipId: number, files: File[]) => void
  onFileRemove: (membershipId: number, fileId: string) => void
}

export const ParticipantFileRow = ({
  participant,
  onFileDrop,
  onFileRemove,
}: ParticipantFileRowProps) => {
  // Calculate current total size of existing files for this participant
  const currentTotalSize = participant.files.reduce((sum, f) => sum + f.file.size, 0)

  const handleDrop = (files: File[]) => {
    const validFiles: File[] = []
    const invalidTypeFiles: File[] = []
    const oversizedFiles: File[] = []

    files.forEach((file) => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        invalidTypeFiles.push(file)
      } else if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file)
      } else {
        validFiles.push(file)
      }
    })

    // Check if adding valid files would exceed the total size limit per participant
    const newFilesTotalSize = validFiles.reduce((sum, f) => sum + f.size, 0)
    const wouldExceedTotalLimit = currentTotalSize + newFilesTotalSize > MAX_TOTAL_SIZE_PER_PARTICIPANT

    // Show error messages for rejected files
    if (invalidTypeFiles.length > 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.training_detail.employees_table.import_certificates_invalid_file_type'),
      })
    }

    if (oversizedFiles.length > 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.training_detail.employees_table.import_certificates_file_too_large'),
      })
    }

    if (wouldExceedTotalLimit) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.training_detail.employees_table.import_certificates_total_size_exceeded'),
      })
      return
    }

    if (validFiles.length > 0) {
      onFileDrop(participant.membershipId, validFiles)
    }
  }

  return (
    <Box gap="s16" paddingY="s12">
      <Box flexDirection="row" alignItems="center" gap="s12">
        <Box flexGrow>
          <Text weight="medium" size="300">
            {participant.employeeName}
          </Text>
        </Box>
      </Box>

      {/* Uploaded files */}
      {participant.files.length > 0 && (
        <Box gap="s8">
          {participant.files.map((fileUpload) => (
            <FileChip
              key={fileUpload.id}
              fileUpload={fileUpload}
              onRemove={() => onFileRemove(participant.membershipId, fileUpload.id)}
            />
          ))}
        </Box>
      )}

      <DropzoneBox
        accept={acceptedContentTypes(ACCEPTED_FILE_TYPES)}
        iconName="file-upload"
        message={i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.import_certificates_dropzone_description'
        )}
        multiple
        onDrop={handleDrop}
      />
    </Box>
  )
}

interface FileChipProps {
  fileUpload: FileUpload
  onRemove: () => void
}

const FileChip = ({ fileUpload, onRemove }: FileChipProps) => {
  const { file, status, progress, error } = fileUpload

  return (
    <Box
      border={{ all: { color: status === FileStatus.ERROR ? 'radicalRed400' : 'grey400', style: 'solid', width: 's1' } }}
      borderRadius={{ all: 'abs008' }}
      gap="s8"
      paddingY="s12"
      paddingX="s16"
    >
      <Box flexDirection="row" gap="s16" alignItems="center" justifyContent="spaceBetween">
        <DocumentType mimetype={file.type} />
        <Box gap="s2" flexGrow>
          <Text weight="medium" size="300">
            {file.name}
          </Text>
          <FileStatusDisplay status={status} progress={progress} error={error} file={file} />
        </Box>
        <Button type="secondary" onClick={onRemove} iconName="delete" />
      </Box>
    </Box>
  )
}

interface FileStatusDisplayProps {
  status: FileUpload['status']
  progress: number
  error?: string
  file: File
}

const FileStatusDisplay = ({ status, progress, error, file }: FileStatusDisplayProps) => {
  switch (status) {
    case FileStatus.PENDING:
      return (
        <Text size="200" color="grey600">
          {i18n.t('trainings.trainings_files_modal.pending')}
        </Text>
      )

    case FileStatus.UPLOADING:
      return (
        <ProgressBar
          barColor="purple200"
          progressColor="purple1000"
          percentage={Math.max(Math.round(progress * 100), 1)}
          amount={progress}
          total={1}
        />
      )

    case FileStatus.SUCCESS:
      return (
        <Text size="200" color="viridian1000">
          {formatFileSize(file.size)}
        </Text>
      )

    case FileStatus.ERROR:
      return (
        <Text size="200" color="radicalRed1000">
          {error || i18n.t('trainings.trainings_files_modal.failed_to_upload')}
        </Text>
      )

    default:
      return null
  }
}
